import { z } from "zod";
import { customerCatalogItemViewSchema } from "./domain-views.js";
// The card hydration route of the mobile conversation (CHT-728; contract section 3.5, ADR 0014; route MC-B1).
//
// A card in a Stream message holds only part REFERENCES (ids and a short label). The app reads the live entity by id before
// it shows any status. For catalog items one request reads up to 12 ids. Other entities have their own customer routes
// (concierge request, payment request, conversation).
//
// Rule: an id that is not a published item and an id that does not exist give the SAME answer (it is listed in
// `unavailableIds`), so the route does not tell whether an unpublished item exists.
/** The most ids of one catalog batch read. A message holds at most 12 references (`MESSAGE_ENVELOPE_LIMITS.maxRefs`). */
export const CUSTOMER_CATALOG_BATCH_MAX_IDS = 12;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
export const customerCatalogItemsBatchSchema = z.strictObject({
    /** Published items, in the order of the request. */
    items: z.array(customerCatalogItemViewSchema).max(CUSTOMER_CATALOG_BATCH_MAX_IDS),
    /** Requested ids that are not available to this customer: missing, unpublished or retired. */
    unavailableIds: z.array(z.string().regex(UUID)).max(CUSTOMER_CATALOG_BATCH_MAX_IDS),
});
// --- what a card reads (wire schemas) -----------------------------------------------------------------------------------------
//
// The server views above use `z.date()` and strict objects, so a view arrives as JSON with ISO text for every time. A card reads
// only a few fields of each entity. These schemas name exactly those fields, accept JSON times (`z.coerce.date`) and ignore the
// keys a card does not use (non-strict). They never widen what the SERVER sends: the server views stay strict and fail closed.
const wireId = z.string().min(1).max(64);
const wireCode = z.string().min(1).max(64);
const wireInstant = z.coerce.date();
export const cardCatalogItemSchema = z.object({
    id: wireId,
    itemType: wireCode,
    name: z.string(),
    slug: z.string(),
    shortDescription: z.string().nullable(),
    location: z.object({
        locality: z.string().nullable(),
        region: z.string().nullable(),
        countryCode: z.string().nullable(),
    }),
    pricing: z.object({
        model: wireCode,
        currency: z.string().nullable(),
        amountMinor: z.number().nullable(),
        minAmountMinor: z.number().nullable(),
        maxAmountMinor: z.number().nullable(),
        unit: wireCode.nullable(),
        terms: z.string().nullable(),
    }),
    categories: z.array(z.object({ code: wireCode, name: z.string(), isPrimary: z.boolean() })),
});
export const cardCatalogItemsBatchSchema = z.object({
    items: z.array(cardCatalogItemSchema).max(CUSTOMER_CATALOG_BATCH_MAX_IDS),
    unavailableIds: z.array(z.string().regex(UUID)).max(CUSTOMER_CATALOG_BATCH_MAX_IDS),
});
export const cardConciergeRequestSchema = z.object({
    id: wireId,
    requestType: wireCode,
    statusCode: wireCode,
    title: z.string(),
    partySize: z.number().nullable(),
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
    customerStatusSummary: z.string().nullable(),
    pendingQuestion: z.string().nullable(),
    paymentRequestId: wireId.nullable(),
    updatedAt: wireInstant,
});
export const cardPaymentRequestSchema = z.object({
    id: wireId,
    status: wireCode,
    amountMinor: z.number(),
    currency: wireCode,
    description: z.string(),
    conciergeRequestId: wireId.nullable(),
    expiresAt: wireInstant,
    // Optional so an older server answer still parses; a missing flag reads as false (fail closed).
    canStartCheckout: z.boolean().optional(),
    // CHT-756: the catalog item this request was made from (`null` for a quote), so the app can ask for a new request at the current price after
    // `payment.source_changed`. Optional so an older server answer still parses.
    catalogItemId: wireId.nullable().optional(),
});
export const cardConversationSchema = z.object({
    id: wireId,
    statusCode: wireCode,
    openHandoffStatusCode: wireCode.nullable(),
});
/** The trip detail (route 4.8) as a card reads it: the trip and its one itinerary. */
export const cardTripDetailSchema = z.object({
    trip: z.object({
        id: wireId,
        title: z.string(),
        status: wireCode,
        startDate: z.string().nullable(),
        endDate: z.string().nullable(),
        destinations: z.array(z.object({ name: z.string(), countryCode: z.string().nullable() })),
    }),
    itinerary: z
        .object({
        id: wireId,
        title: z.string(),
        itemCount: z.number(),
        days: z.array(z.object({ localDate: z.string(), itemCount: z.number() })),
    })
        .nullable(),
});
